import {
    FunctionComponent,
    PropsWithChildren,
    useEffect,
    useState,
} from 'react';
import { useAuthentication } from '@/hooks/useAuthentication';
import { useNavigate, useLocation } from 'react-router-dom';
import { useIsAuthorized } from '@/hooks/useIsAuthorized';
import UnAuthorized from '@/pages/UnAuthorized';
import { PageWithLayout } from '@/models/page';

type AuthGuardProps = {
    Component: PageWithLayout;
};

export const AuthGuard: FunctionComponent<
    PropsWithChildren<AuthGuardProps>
> = ({ children, Component }) => {
    const {
        isLoggedIn,
        saveAuthDataFromUrl,
        authenticateFromLocalStorageData,
    } = useAuthentication();
    const navigate = useNavigate();
    const location = useLocation();
    const pathname = location.pathname;
    const [isReady] = useState(true);
    const { isProtected } = Component;
    const [localAuthCheckDone, setLocalAuthCheckDone] = useState(false);
    const [isAuthorized] = useIsAuthorized(Component.permissions || []);

    useEffect(() => {
        if (isReady) {
            const authDataSaved = saveAuthDataFromUrl();
            if (authDataSaved) {
                // Remove URL params after saving auth data
                const url = new URL(window.location.href);
                url.searchParams.delete('jwt');
                url.searchParams.delete('refreshCode');
                url.searchParams.delete('validFor');
                url.searchParams.delete('liftOperatorName');
                url.hash = '';
                window.history.replaceState({}, '', url.toString());
            } else {
                authenticateFromLocalStorageData();
            }

            setLocalAuthCheckDone(true);
        }
    }, [isReady]);

    if (localAuthCheckDone) {
        const [loggedIn, error] = isLoggedIn();

        if (loggedIn) {
            if (!isAuthorized) {
                const getLayout = Component.getLayout ?? ((page) => page);

                return <>{getLayout(<UnAuthorized />)}</>;
            }

            return <>{children}</>;
        } else {
            if (error === 'no_jwt') {
                navigate('/login');
            } else {
                navigate(`/login?redirectUrl=${encodeURIComponent(pathname)}`);
            }
        }

        return null;
    }

    if (isProtected) {
        return localAuthCheckDone ? <>{children}</> : null;
    } else {
        return <>{children}</>;
    }
};
